import type { AdminActivityEvent, AgentEventSource, AgentEventType } from '@doneverse/shared'
import { hostFromUrl } from '@doneverse/shared'
import { formatHumanDuration } from '@/lib/utils'

export { hostFromUrl }

/** Rendered wherever a column has no value for this row. */
export const EMPTY_CELL = '—'

/** Whether a viewer gets unredacted activity (titles, URLs, executables).
 *  Mirrors the API's `policyForOfficer` (`apps/api/src/lib/activity-read.ts`):
 *  `admin` and `dsl` get full detail, every other role is redacted. Render-side
 *  redaction is a second layer on top of the API's, and it must follow the same
 *  rule, or it blanks what the API deliberately sent. No role (auth still
 *  resolving) hides rather than reveals. */
export function viewerSeesFullDetail(role: string | undefined): boolean {
  return role === 'admin' || role === 'dsl'
}

/** The two streams that carry a duration and describe a span of work.
 *  `heartbeat` and `url_visit` are excluded on purpose: both are instants with
 *  no duration, so they render as mostly-empty rows. `idle` is opt-in through
 *  the toolbar. Readonly so consumers can't mutate the shared default; spread
 *  it into a fresh array before appending anything. */
export const DEFAULT_EVENT_TYPES: readonly AgentEventType[] = ['foreground_app', 'foreground_domain']

/** A process exit code as Windows writes it: unsigned 32-bit hex, eight
 *  digits. The API stores the code as the agent sent it, which may be a
 *  signed reading of the same bits, so `>>> 0` folds both onto one form:
 *  3762509141 and -532458155 both render `0xE0435555`. ClickUp 14zf25zj4hr. */
export function formatExitCode(code: number): string {
  return `0x${(code >>> 0).toString(16).toUpperCase().padStart(8, '0')}`
}

/** What an `agent_exit` row says in place of an app name. `null` is an agent
 *  that sent no valid code; the crash still happened. */
export function agentExitLabel(code: number | null): string {
  return code === null ? 'Agent crashed (exit code unknown)' : `Agent crashed (exit ${formatExitCode(code)})`
}

const SOURCE_LABELS: Record<AgentEventSource, string> = {
  desktop: 'Desktop',
  extension: 'Browser',
}

/** Which stream produced the row. Read off the event, never off the device:
 *  one device carries both, because the extension joins the agent's already
 *  enrolled device. Deriving this from the platform labels every extension row
 *  Desktop. */
export function sourceLabel(source: AgentEventSource): string {
  return SOURCE_LABELS[source]
}

/** The App cell: what the operating system had in front, and nothing about the
 *  web. The site lives in its own column now that a row can carry both (ClickUp
 *  `86d4bun9p`), so a browser-sourced row contributes no app name here.
 *
 *  Switching over the event type rather than testing which field is populated
 *  is an acceptance criterion: selecting by field presence caused a measured
 *  10% inflation in this codebase before. The `default` case asserts `never`,
 *  so a new event type fails typecheck instead of silently falling through to
 *  the empty cell. */
export function appCell(event: AdminActivityEvent): string {
  switch (event.event_type) {
    case 'foreground_app':
      return event.app_name ?? EMPTY_CELL
    case 'idle':
      return 'Idle'
    case 'schedule_on':
      return 'Schedule on'
    case 'schedule_off':
      return 'Schedule off'
    case 'agent_exit':
      return agentExitLabel(event.exit_code)
    case 'foreground_domain':
    case 'url_visit':
    case 'heartbeat':
      return EMPTY_CELL
    default: {
      const _exhaustive: never = event.event_type
      return EMPTY_CELL
    }
  }
}

/** The Site cell: the host this row was on, resolved from the row alone.
 *
 *  It used to take an already-resolved value, because a merged row borrowed its
 *  site from a different event than its spine. Rows are no longer merged
 *  (ClickUp `86d4c4xf2`), so every row answers for itself and the resolution
 *  lives here with every other cell's.
 *
 *  Switching over the event type rather than testing which field is populated is
 *  the standing rule; selecting by field presence caused a measured 10%
 *  inflation in this codebase before. The `default` case asserts `never`, so a
 *  new event type fails typecheck rather than silently rendering empty.
 *
 *  Stored beats derived wherever both are possible: a stored domain is
 *  authoritative and a derived host is a best effort (ClickUp `86d4bemeh`).
 *
 *  - `foreground_app` carries no domain and never will. The agent's sample is
 *    app, title and URL (ClickUp `86d4c2k75`, doer-apps PR #38), and the ingest
 *    route stores a domain only when a producer sends one, so the host is
 *    derived from the URL.
 *
 *    **This branch is live only where the capture is compiled in.** The native
 *    read sits behind doer-apps' `urlCaptureEnabled` build flag, which every
 *    release build turns off unless the installer script is passed the enabling
 *    switch. `build-windows-installer-staging.yml` passes `-EnableURLCapture`
 *    (doer-apps PR #40, merged 2026-09-22 06:53 UTC);
 *    `build-windows-installer.yml` does not, so the production installer
 *    captures nothing. macOS returns `URLUnsupported` either way.
 *
 *    Measured on stage the same day, exact counts across all four devices that
 *    reported: `748f06e9` 118 of 904 `foreground_app` rows carried a URL, and
 *    the other three carried 0 of 933, 0 of 492 and 0 of 145. So one machine,
 *    which had a hand-built agent before the staging installer existed. Nothing
 *    renders wrongly for anyone: production has 0 devices and 0 activity rows.
 *    Do not read the one populated device as the fleet.
 *
 *    Live **for an `admin` or a `dsl`**, and that distinction now matters
 *    rather than being theoretical. Deriving from `url` rather than from `domain` is not
 *    neutral on who can see it: `url` is in `SENSITIVE_EVENT_FIELDS` and the
 *    API nulls it for an `escalation_officer`, while `domain` is deliberately
 *    left visible as "materially less revealing". So a desktop row's host is a
 *    placeholder for that role while the extension row beside it shows its
 *    site. Still nothing observable turns on it, because
 *    `/admin/activity-log` admits only `admin` and `dsl`, who both get full
 *    detail, so no redacted viewer opens this table. The durable fix is to derive the host once on ingest and
 *    store it in `domain`, which survives redaction without a second copy of
 *    this rule at the producer. Done in 86d4a9r1f: ingest now stores it.
 *  - `foreground_domain` carries the domain the extension resolved. Read it.
 *  - `url_visit` never carries a domain, deliberately: the producer keeps the
 *    domain on the session row and calls the visit row "a presence log". Derive.
 *  - `idle` describes absence, and a `heartbeat` is not a span this table
 *    renders, so neither shows a site even though a heartbeat often carries a
 *    domain (about one row in five on stage).
 *
 *  Two of those arms are unreachable from the only caller rather than merely
 *  rare. `AdminActivityLog` asks `eventTypesForFilters` what to request, and
 *  the union of everything it returns, across every idle setting,
 *  is `foreground_app`, `foreground_domain`, `idle` and `agent_exit`. Each
 *  setting asks for a subset of those four, never an addition. No `heartbeat`
 *  or `url_visit` row is ever fetched into this table by any of them. Both arms are written
 *  for the exhaustiveness the `never` default enforces, and because that union
 *  is one edit away from growing. */
export function siteCell(event: AdminActivityEvent): string {
  switch (event.event_type) {
    case 'foreground_app':
      return hostFromUrl(event.url) ?? EMPTY_CELL
    case 'foreground_domain':
    case 'url_visit':
      return event.domain ?? hostFromUrl(event.url) ?? EMPTY_CELL
    case 'idle':
    case 'heartbeat':
    case 'schedule_on':
    case 'schedule_off':
    case 'agent_exit':
      return EMPTY_CELL
    default: {
      const _exhaustive: never = event.event_type
      return EMPTY_CELL
    }
  }
}

/** True when `window_title` describes the span this row measures.
 *  Idle is the exception: its title names whatever was frontmost when the
 *  machine went away, an instant outside the span, so it is dropped.
 *  `TimeRangeActivityPanel` imports this rather than keeping its own copy, so
 *  the rule can't drift between the two views. The `default` case asserts
 *  `never`, so a new event type fails typecheck instead of silently
 *  inheriting `false`. */
export function titleDescribesOwnSpan(eventType: AgentEventType): boolean {
  switch (eventType) {
    case 'idle':
    case 'schedule_on':
    case 'schedule_off':
    case 'agent_exit':
      return false
    case 'foreground_app':
    case 'foreground_domain':
    case 'url_visit':
    case 'heartbeat':
      return true
    default: {
      const _exhaustive: never = eventType
      return false
    }
  }
}

export function titleCell(event: AdminActivityEvent): string {
  if (!titleDescribesOwnSpan(event.event_type)) return EMPTY_CELL
  return event.window_title ?? EMPTY_CELL
}

export type UrlSemantics = 'none' | 'exact' | 'session-last'

/** What a row's `url` means, decided by the row's type and never by whether the
 *  field happens to be populated — the standing rule, and the reason
 *  `appCell` and `siteCell` above switch too.
 *
 *  - `'exact'` — the URL and the row describe the same thing, so it needs no
 *    caveat. `url_visit` marks the instant of one committed navigation.
 *    `foreground_app` joined it when the agent gained a native URL read
 *    (ClickUp `86d4c2k75`, doer-apps PR #38): that agent treats a URL change as
 *    an EVENT BOUNDARY, closing the current event and opening a new one, so
 *    `url`, `occurred_at` and `duration_seconds` all cover one page. Verified
 *    at the producer rather than inferred from the ticket — `monitor.go`
 *    backfills a late-resolving URL into the open event in place and only a
 *    real navigation closes it. Corroborated in the data too: on the one
 *    capturing device the desktop stream emitted more URL-bearing rows than the
 *    extension emitted navigations, which is what a boundary predicts and
 *    'session-last' does not.
 *
 *    Exact to the poll, which is every 3 seconds by default
 *    (`config.go`'s `PollIntervalSeconds`). A page opened and left inside one
 *    tick is never sampled, so its seconds land on a neighbouring event. That
 *    does not make this the wrong label against `'session-last'`; it means
 *    "covers one page" is true of observed pages.
 *  - `'session-last'` — the row spans a browser session, and the URL is the
 *    LAST one seen during it while `occurred_at` is the session's start (see
 *    ClickUp 86d4at71y). 164 of 342 joinable stage sessions changed URL
 *    mid-session, so read as "the URL at `occurred_at`" this is wrong about as
 *    often as it is right. A view that renders it owes the reader that
 *    distinction. That figure is inherited from 86d4at71y, not re-derived here.
 *    **The two browser streams now disagree about this**, and correctly: an
 *    extension session row is `'session-last'` while a desktop row covering the
 *    same moment is `'exact'`, because they are bounded differently.
 *  - `'none'` — the type has no URL of its own. An `idle` or `heartbeat` row
 *    that carries one is describing something outside the span it covers, the
 *    same way `idle` carries a `window_title` from before the span began.
 *
 *  This returns the semantics rather than a rendered string on purpose: the
 *  rule is shared so it can't drift between the two views, but how each view
 *  marks `'session-last'` is its own call — a stacked list has room for a
 *  prefix, a fixed-layout table may not.
 *
 *  The `default` case asserts `never`, so a new event type fails typecheck
 *  instead of silently inheriting a rendering. */
export function urlSemantics(eventType: AgentEventType): UrlSemantics {
  switch (eventType) {
    case 'url_visit':
    case 'foreground_app':
      return 'exact'
    case 'foreground_domain':
      return 'session-last'
    case 'idle':
    case 'heartbeat':
    case 'schedule_on':
    case 'schedule_off':
    case 'agent_exit':
      return 'none'
    default: {
      const _exhaustive: never = eventType
      return 'none'
    }
  }
}

/** The Path cell: everything after the host, or the placeholder.
 *
 *  Returns the rendered text and the `title` separately because they differ,
 *  and the difference is the point. The cell shows the path; the `title` holds
 *  the whole URL, and on a session row the caveat as well.
 *
 *  WHY THE PATH RATHER THAN THE WHOLE URL, which is what `86d4at80g`'s design
 *  session decided on paper. Measured in real Chrome 153 at this table's own
 *  font, with the harness validated by reproducing `.colDateTime`'s committed
 *  172.47px worst case to the hundredth of a pixel first:
 *
 *    the sample pilot URL is 100 characters and needs 677.77px
 *    its origin is 29 of those characters
 *    a full-URL cell at 16rem shows 33 — so 29 repeat the Site column and the
 *      path, the only thing this column can say that Site cannot, gets four
 *    the whole of a short ClickUp path fits a 12rem cell, at 23 characters
 *
 *  Buying enough width for a full-URL rendering to clear the origin means
 *  24rem and a `minWidth` of 1880, and it still truncates most real URLs. So
 *  the origin is dropped here and the Site column beside it carries the host.
 *  Nothing is lost: the whole URL is on the `title`.
 *
 *  Selected through `urlSemantics`, never by asking whether `url` is populated.
 *  That is the standing rule, and it is what made this cell compose with
 *  `86d4c2k75` at no cost. That ticket's agent half shipped (doer-apps PR #38),
 *  `urlSemantics('foreground_app')` moved from `'none'` to `'exact'`, and
 *  desktop rows began rendering with **no edit to this function at all** — the
 *  whole change was one case statement and the tests that pinned the old
 *  answer. Worth keeping in mind before anyone is tempted to shortcut the type
 *  rule for a "quick" cell.
 *
 *  Both `'exact'` and `'session-last'` now reach this table, and they mean
 *  different things on rows that sit next to each other. `AdminActivityLog`
 *  asks `eventTypesForFilters` what to request and the union of everything it
 *  returns is `foreground_app`, `foreground_domain`, `idle` and `agent_exit`,
 *  so no `url_visit` row is ever fetched here — the same "unreachable from the only
 *  caller rather than merely rare" note `siteCell` carries.
 *
 *  So a desktop row's path is exact and its hover carries no caveat, while the
 *  extension session row covering the same browser moment is `'session-last'`
 *  and its hover says so. The two disagreeing is correct: they are bounded
 *  differently at the producer. Nothing marks the distinction in the visible
 *  cell, which is deliberate for a cell this narrow and worth knowing rather
 *  than discovering.
 *
 *  - `'session-last'` — the row spans a browser session, `occurred_at` is its
 *    START and `url` is the LAST address seen during it. Rendered bare under
 *    that timestamp it reads as "where they were at 09:00", which 164 of 342
 *    joinable stage sessions contradict (figure inherited from ClickUp
 *    `86d4at71y`, not re-derived here). `TimeRangeActivityPanel` spends a
 *    `Last URL:` prefix on this; a fixed-layout cell that truncates at all
 *    cannot afford one, so the caveat goes on the `title`. Same rule, each view
 *    marking it its own way, which is why `urlSemantics` returns meaning rather
 *    than markup.
 *  - `'exact'` — the URL and the row cover the same page, so `title` is the URL
 *    with no caveat. This is what a desktop row returns now that the agent
 *    bounds its events on URL change (`86d4c2k75`), and it is the common case
 *    in the table. `url_visit` also returns it but never reaches here.
 *  - `'none'` — the type has no URL of its own, so nothing renders even when
 *    the field is populated.
 *
 *  `fullDetail` is the second layer the API's own comment asks for, and the
 *  reason this cell takes a viewer where `siteCell` does not. `url` is in
 *  `SENSITIVE_EVENT_FIELDS`; `domain` is deliberately not, because a full path
 *  with a query string reveals materially more than a host. The API nulls it
 *  for every role outside `viewerSeesFullDetail` (admin and dsl), so a
 *  populated `url` reaching any other viewer means layer one failed and nothing
 *  derived from it should render. `/admin/activity-log` admits admin and dsl
 *  only, so the redacted branch is unreachable in the shipped app; it is kept
 *  because the route gate is one edit away from changing. See the redaction
 *  note on `siteCell`. */
export function pathCell(event: AdminActivityEvent, fullDetail: boolean): { text: string; title?: string } {
  const text = pathText(event, fullDetail)
  if (text === EMPTY_CELL) return { text: EMPTY_CELL }
  return {
    text: truncateMiddle(text),
    title:
      urlSemantics(event.event_type) === 'session-last'
        ? `Last URL in the session: ${event.url}`
        : event.url!,
  }
}

/** The same path `pathCell` shows, before it is shortened to fit.
 *
 *  Split out so the CSV export can reuse the redaction and event-type rules
 *  without reimplementing them — three guards that must not drift between two
 *  callers — while skipping `truncateMiddle`, which exists only because
 *  `.colPath` is a fixed 12rem and a file has no such budget.
 *
 *  That is not a marginal difference. `truncateMiddle` keeps 19 characters, and
 *  measured over 5,000 stage rows carrying a URL the median path is 58
 *  characters and the 90th percentile is 101, so **93% of real paths come back
 *  ellipsised**. Rendered that is honest — the cell visibly says it was cut, and
 *  the whole URL is one hover away. Written to a CSV it would be neither: there
 *  is no hover, and a column of `/mail/u/…#inbox` cannot be grouped or pivoted,
 *  which is the entire reason the file exists. */
export function pathText(event: AdminActivityEvent, fullDetail: boolean): string {
  if (!fullDetail || !event.url) return EMPTY_CELL
  const semantics = urlSemantics(event.event_type)
  switch (semantics) {
    case 'exact':
    case 'session-last':
      return pathOf(event.url)
    case 'none':
      return EMPTY_CELL
    default: {
      const _exhaustive: never = semantics
      return EMPTY_CELL
    }
  }
}

/** Head and tail kept, ellipsis between, because the identifying token in a
 *  real path is at the END and `text-overflow: ellipsis` cuts exactly that.
 *
 *  Measured over the rows this column renders on stage (4,000 rows, 818
 *  distinct pages; figures from the PR #1317 review, not re-derived here, and
 *  each labelled with the budget it belongs to because this file's own standard
 *  is that a figure carried across a changed configuration is arithmetic
 *  presented as measurement):
 *
 *    cutting the tail, 23 chars      402 of 818 cells   60.9% of rows ambiguous
 *    middle, head 11 tail 11, 23     708 of 818         15.1%
 *    middle, head  6 tail 12, 19     700 of 818         15.4%
 *    middle, head  8 tail 10, 19     699 of 818         15.5%   <- ships
 *    middle, head  9 tail  9, 19     699 of 818         16.9%
 *
 *  The 6/12 row is in the table for the same reason the tail-only row is: so
 *  nobody re-derives it from the collision count and picks it. It scores 0.1pp
 *  BETTER than what ships and is rejected anyway, because 6 characters of head
 *  renders `/sprea…` — it does not even reach `/spreads`, let alone a document
 *  id, so it loses the shape this rule exists to protect. Ranking these by
 *  collision count alone picks the wrong one twice.
 *
 *  So this rule leaves 15.5% where cutting the tail left 60.9%. The 0.4pp
 *  between it and an 11/11 split is the cost of FITTING THE BOX, not of a
 *  budget choice anyone could reverse: 23 characters needs head 11 tail 11,
 *  which overflows the 160px content box, and the column wide enough to hold it
 *  costs a `minWidth` of 1880. Width is a weak lever anyway — even at 60
 *  visible characters, far past that, cutting the tail still leaves 27.4%.
 *
 *  Cutting the HEAD instead scores better still (10.7%) and is rejected anyway:
 *  a Google Sheets path identifies by its document id, which is early, so a
 *  tail-only rule breaks the shape a head-only rule gets right. Middle is the
 *  only rule that reads for both.
 *
 *  THE HONEST LIMIT, and it is not one vendor's quirk. Any path whose
 *  identifying token is followed by a query string loses that token from BOTH
 *  windows. A ClickUp task renders `/t/90165…/86d47a35k` and keeps its id, but
 *  the same task with `?whitelabelRefreshTokenMeta=…` renders
 *  `/t/90165…22:true%7D` and loses it; 21 spreadsheets all render
 *  `/spreads…id=0#gid=0`. Measured: 45 of 818 distinct pages regress this way,
 *  3.9% of rows, against roughly 1,814 rows rescued — a 9:1 trade, which is why
 *  the rule still ships. `title` recovers every one of them.
 *
 *  THE BUDGET IS MEASURED, and it is a character count standing in for pixels.
 *  Measured in real Chrome at the cell's own font against its 160px content
 *  box, the widest output across five real path shapes (GitHub, ClickUp, Zoho,
 *  Gmail, Sheets):
 *
 *    head 10 tail 10   165.81px   OVERFLOWS
 *    head  9 tail 11   171.98px   OVERFLOWS
 *    head  9 tail 10   159.06px   fits by 0.94px — under this file's 2.5px bar
 *    head  9 tail  9   151.36px   fits by 8.64px
 *    head  8 tail 10   147.89px   fits by 12.11px   <- shipped
 *
 *  Gmail is the binding shape, its thread ids being uppercase-heavy. The tail
 *  is favoured over the head at equal cost because it is the identifying end in
 *  three of the four shapes that are not Sheets.
 *
 *  `.truncate` stays on the cell as the backstop, and that is not redundant: a
 *  path of unusually wide glyphs can still overflow, and CSS then cuts its tail,
 *  degrading that row to the old behaviour. Nothing is lost when it happens —
 *  the whole URL is on the `title` — but it is why the budget is conservative
 *  rather than sized to the average.
 *
 *  The cost of a character rule: real paths fit 21 to 23 characters, so 19
 *  truncates 2 to 4 characters earlier than the CSS alone would, and a path of
 *  unusually narrow glyphs is truncated earlier still. That is the price of
 *  keeping the tail, paid in a few characters of head. */
function truncateMiddle(path: string): string {
  const HEAD = 8
  const TAIL = 10
  if (path.length <= HEAD + TAIL + 1) return path
  return `${path.slice(0, HEAD)}…${path.slice(-TAIL)}`
}

/** Everything after the host: path, query and fragment.
 *
 *  The fragment is kept because on an SPA route it is the whole address, and a
 *  path of `/board` would be the same cell for every card on the board.
 *
 *  A URL with no path of its own still renders `/`, so the cell never reads as
 *  "no value" when the value is the site root. That comes from the parser
 *  rather than from anything here: `URL.pathname` is `/` for `https://github.com`
 *  and is never the empty string, which an earlier revision of this function
 *  guarded against with a `|| '/'` that could not fire.
 *
 *  An unparseable URL falls back to the raw string rather than the placeholder.
 *  It is still the only record of where the doer was, and hiding it would say
 *  "no URL" when the truth is "this one would not split". In practice the
 *  branch is unreachable for stored rows — `logUrlVisit` drops anything that
 *  fails to parse before enqueueing it, and 0 of 3,978 stored stage URLs failed
 *  to parse when that was checked in review — which is the same reasoning
 *  `hostFromUrl` records for its own null branch. */
function pathOf(url: string): string {
  try {
    const u = new URL(url)
    return `${u.pathname}${u.search}${u.hash}`
  } catch {
    return url
  }
}

export function durationCell(event: AdminActivityEvent): string {
  return event.duration_seconds === null ? EMPTY_CELL : formatHumanDuration(event.duration_seconds)
}

/** Whole seconds between the row ending on the device and the platform first
 *  storing it (ClickUp `86d4b8apv`). The agent buffers rows while it is
 *  offline and flushes them on reconnect, so a large value is an outage.
 *
 *  Measured from the END, `occurred_at + duration_seconds`, because
 *  `occurred_at` is backdated to the event's start: from the start, every row
 *  would carry its own duration as delay. A row with no duration is an
 *  instant. Prod's noise floor is about ±2 minutes (p50 13 to 21s over 7 days
 *  on 2026-10-19), and the negative tail is device clock skew, so a negative
 *  is floored to 0 rather than shown as the platform receiving a row before
 *  it ended.
 *
 *  `null` only when a timestamp will not parse, which stored rows never do. */
export function receiptDelaySeconds(event: AdminActivityEvent): number | null {
  const ended = Date.parse(event.occurred_at) + (event.duration_seconds ?? 0) * 1000
  const delayMs = Date.parse(event.received_at) - ended
  if (!Number.isFinite(delayMs)) return null
  return Math.max(0, Math.round(delayMs / 1000))
}

/** The Delay cell, in the Duration column's formatter so the two read alike.
 *  The table and the CSV both render through here. */
export function delayCell(event: AdminActivityEvent): string {
  const seconds = receiptDelaySeconds(event)
  return seconds === null ? EMPTY_CELL : formatHumanDuration(seconds)
}

/** The label under the Delay of a row the agent held until it declared idle
 *  (`held_for_idle`, ClickUp 14zf25zktwk), and its hover. The delay itself is
 *  left as it is: it is the real gap, and the label says why it is there.
 *  The hover does not say "5 minutes": production has held rows that arrived
 *  1.5 to 5 minutes after the Idle row started, cause not yet traced. */
export const HELD_FOR_IDLE_LABEL = 'idle check'
export const HELD_FOR_IDLE_TITLE =
  "Held by the agent's idle check: it waits for a stretch without input before it can close this row, so the row was sent when the doer went idle. Expected, not a delivery problem. Duration is unaffected."

/** Hostname is stored on the device row, not the event, so it is joined here.
 *  A deleted device leaves no hostname, so show a short id rather than an
 *  empty cell that reads like missing data. Revoked devices are NOT the case
 *  this covers: `listAgentDevicesForUser` orders them last but never filters
 *  them out (`packages/db/src/queries/agent-devices.ts:147-160`), so a revoked
 *  device still resolves its hostname. */
export function computerCell(deviceId: string, hostnameById: Map<string, string>): string {
  return hostnameById.get(deviceId) ?? deviceId.slice(0, 8)
}
